import React from 'react'
import { motion } from 'framer-motion'
import { Download, Code, Wifi, Settings, CheckCircle, AlertCircle } from 'lucide-react'

const HowToLoadProgram = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-4xl mx-auto p-6"
    >
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-white mb-4">How to Load Program</h1>
        <p className="text-gray-300 text-lg">
          Complete guide to upload the EZ Control program to your NodeMCU ESP8266
        </p>
      </div>

      {/* Prerequisites Section */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/5 backdrop-blur-md rounded-lg p-6 mb-6 border border-white/10"
      >
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
          Prerequisites
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-300">Arduino IDE 2.0 or later</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-300">NodeMCU ESP8266 board</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-300">USB Type-C cable</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-300">ESP8266 board package</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-300">WiFi credentials</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-300">Required libraries</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Step-by-Step Guide */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-6"
      >
        {/* Step 1 */}
        <div className="bg-white/5 backdrop-blur-md rounded-lg p-6 border border-white/10">
          <div className="flex items-center mb-4">
            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">1</div>
            <h3 className="text-lg font-semibold text-white">Install Arduino IDE & ESP8266 Support</h3>
          </div>
          <div className="space-y-3 text-gray-300">
            <p>1. Download and install <a href="https://www.arduino.cc/en/software" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">Arduino IDE</a></p>
            <p>2. Open Arduino IDE → File → Preferences</p>
            <p>3. Add this URL to "Additional Board Manager URLs":</p>
            <div className="bg-gray-800 p-3 rounded font-mono text-sm">
              https://arduino.esp8266.com/stable/package_esp8266com_index.json
            </div>
            <p>4. Go to Tools → Board → Boards Manager</p>
            <p>5. Search for "ESP8266" and install "ESP8266 by ESP8266 Community"</p>
          </div>
        </div>

        {/* Step 2 */}
        <div className="bg-white/5 backdrop-blur-md rounded-lg p-6 border border-white/10">
          <div className="flex items-center mb-4">
            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">2</div>
            <h3 className="text-lg font-semibold text-white">Download & Configure Code</h3>
          </div>
          <div className="space-y-3 text-gray-300">
            <p>1. Download the <a href="https://github.com/PAPPURAJ/Ez-Control-Microcontroller/blob/main/Ez%20Control%20NodeMCU%20Code.ino" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">EZ Control NodeMCU Code.ino</a> file</p>
            <p>2. Open the .ino file in Arduino IDE</p>
            <p>3. Update WiFi credentials in the code:</p>
            <div className="bg-gray-800 p-3 rounded font-mono text-sm">
              const char* ssid = "YOUR_WIFI_NAME";<br/>
              const char* password = "YOUR_WIFI_PASSWORD";
            </div>
            <p>4. Set board settings:</p>
            <div className="bg-gray-800 p-3 rounded font-mono text-sm">
              Board: "NodeMCU 1.0 (ESP-12E Module)"<br/>
              Upload Speed: "115200"<br/>
              Port: Select your device port
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div className="bg-white/5 backdrop-blur-md rounded-lg p-6 border border-white/10">
          <div className="flex items-center mb-4">
            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">3</div>
            <h3 className="text-lg font-semibold text-white">Upload Code to NodeMCU</h3>
          </div>
          <div className="space-y-3 text-gray-300">
            <p>1. Connect NodeMCU to computer via USB cable</p>
            <p>2. Select the correct COM port in Arduino IDE</p>
            <p>3. Click the Upload button (→) or press Ctrl+U</p>
            <p>4. Wait for upload to complete</p>
            <p>5. Open Serial Monitor (Tools → Serial Monitor)</p>
            <p>6. Set baud rate to 115200</p>
          </div>
        </div>

        {/* Step 4 */}
        <div className="bg-white/5 backdrop-blur-md rounded-lg p-6 border border-white/10">
          <div className="flex items-center mb-4">
            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">4</div>
            <h3 className="text-lg font-semibold text-white">Verify & Test</h3>
          </div>
          <div className="space-y-3 text-gray-300">
            <p>1. Check Serial Monitor for successful messages:</p>
            <div className="bg-gray-800 p-3 rounded font-mono text-sm">
              WiFi connected!<br/>
              IP address: 192.168.1.xxx<br/>
              mDNS responder started<br/>
              HTTP server started
            </div>
            <p>2. Note the IP address displayed</p>
            <p>3. Open web browser and navigate to the IP address</p>
            <p>4. You should see the EZ Control status page</p>
          </div>
        </div>
      </motion.div>

      {/* Troubleshooting Section */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/5 backdrop-blur-md rounded-lg p-6 mt-6 border border-white/10"
      >
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <AlertCircle className="w-6 h-6 text-yellow-400 mr-2" />
          Common Issues & Solutions
        </h2>
        <div className="space-y-4">
          <div className="border-l-4 border-yellow-400 pl-4">
            <h4 className="font-semibold text-white mb-2">Upload Failed</h4>
            <p className="text-gray-300 text-sm">Try pressing the FLASH button on NodeMCU before uploading, or check USB cable connection.</p>
          </div>
          <div className="border-l-4 border-yellow-400 pl-4">
            <h4 className="font-semibold text-white mb-2">WiFi Connection Failed</h4>
            <p className="text-gray-300 text-sm">Verify WiFi credentials and ensure the network is accessible. Check signal strength.</p>
          </div>
          <div className="border-l-4 border-yellow-400 pl-4">
            <h4 className="font-semibold text-white mb-2">Port Not Found</h4>
            <p className="text-gray-300 text-sm">Install USB-to-Serial drivers for your operating system. Try different USB cable.</p>
          </div>
        </div>
      </motion.div>

      {/* Resources Section */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/5 backdrop-blur-md rounded-lg p-6 mt-6 border border-white/10"
      >
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Download className="w-6 h-6 text-blue-400 mr-2" />
          Additional Resources
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <a 
              href="https://github.com/PAPPURAJ/Ez-Control-Microcontroller/blob/main/CAR_CONTROL_SETUP.md" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block p-3 bg-gray-800 rounded hover:bg-gray-700 transition-colors"
            >
              <h4 className="font-semibold text-white">📋 Setup Guide</h4>
              <p className="text-gray-400 text-sm">Detailed hardware setup and wiring instructions</p>
            </a>
            <a 
              href="https://github.com/PAPPURAJ/Ez-Control-Microcontroller/blob/main/README.md" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block p-3 bg-gray-800 rounded hover:bg-gray-700 transition-colors"
            >
              <h4 className="font-semibold text-white">📖 Full Documentation</h4>
              <p className="text-gray-400 text-sm">Complete project documentation and API reference</p>
            </a>
          </div>
          <div className="space-y-3">
            <a 
              href="https://ezcontrol.pappuraj.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block p-3 bg-gray-800 rounded hover:bg-gray-700 transition-colors"
            >
              <h4 className="font-semibold text-white">🌐 Live Demo</h4>
              <p className="text-gray-400 text-sm">Test the web interface before building</p>
            </a>
            <a 
              href="https://github.com/PAPPURAJ/Ez-Control-Microcontroller" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block p-3 bg-gray-800 rounded hover:bg-gray-700 transition-colors"
            >
              <h4 className="font-semibold text-white">💻 Source Code</h4>
              <p className="text-gray-400 text-sm">View the complete project on GitHub</p>
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default HowToLoadProgram
